import { useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from './Toast';
import { ApiError } from '../api/types';
import { theme } from '../theme';
import { Icon } from './Icon';
import { newClientId } from '../utils/ids';
import { celebrateBig, celebrateSmall } from '../utils/confetti';
import { createdAtLocalDay, recordedDaySet } from '../utils/recording-streak';
import {
  FREEZE_PRICE,
  chestFor,
  claimableChest,
  coinsBalance,
  freezeInventory,
  generateQuest,
  monthKeyOf,
  questState,
  toIso,
  weekDays,
  weekMonday,
  Quest,
} from '../utils/game';

const WEEK_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const dayLabel = (iso: string) => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '·';
  return WEEK_LABELS[(new Date(+m[1], +m[2] - 1, +m[3]).getDay() + 6) % 7];
};

/** Ячейки прогресса квеста днями: пройденные — подчёрк тушью, будущие — пунктир. */
function questCells(
  quest: Quest,
  operations: import('../api/types').Operation[],
  mondayIso: string,
  todayIso: string,
): { label: string; done: boolean }[] | null {
  if (quest.kind === 'cap_week') return null; // лимит суммы — прогресс не днями
  const days = weekDays(mondayIso);
  let doneDays: string[];
  if (quest.kind === 'record_days') {
    const recorded = recordedDaySet(operations);
    doneDays = days.filter((d) => recorded.has(d) && d <= todayIso);
  } else {
    const spent = new Set(operations.filter((o) => o.category === quest.cat).map((o) => o.date));
    doneDays = days.filter((d) => d < todayIso && !spent.has(d));
  }
  doneDays = doneDays.slice(0, quest.target);
  const upcoming = days.filter((d) => d >= todayIso).slice(0, quest.target - doneDays.length);
  const cells = [
    ...doneDays.map((d) => ({ label: dayLabel(d), done: true })),
    ...upcoming.map((d) => ({ label: dayLabel(d), done: false })),
  ];
  while (cells.length < quest.target) cells.push({ label: '·', done: false });
  return cells.slice(0, quest.target);
}

/** Сундук-объект 46×38, рисуется рамками (Антверпен 2.0 §1). */
function ChestGlyph({ state }: { state: 'sealed' | 'ready' | 'open' }) {
  const base: React.CSSProperties = {
    width: 46,
    height: 38,
    borderRadius: theme.radius.sm,
    position: 'relative',
    flexShrink: 0,
  };
  if (state === 'ready') {
    return (
      <div style={{ ...base, background: theme.colors.ink }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: 10, borderTop: `1.5px solid ${theme.colors.paper}`, transform: 'rotate(-4deg)' }} />
        <div style={{ position: 'absolute', left: '50%', top: 16, width: 6, height: 8, marginLeft: -3, border: `1.5px solid ${theme.colors.paper}`, borderRadius: 1 }} />
      </div>
    );
  }
  if (state === 'open') {
    return (
      <div style={{ ...base, border: `1.5px solid ${theme.colors.green}` }}>
        <div style={{ position: 'absolute', left: -2, right: 14, top: -7, borderTop: `1.5px solid ${theme.colors.green}`, transform: 'rotate(-14deg)' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, top: 12, borderTop: `1.5px dashed ${theme.colors.green}`, opacity: 0.4 }} />
      </div>
    );
  }
  return (
    <div style={{ ...base, border: `1.5px solid ${theme.colors.ink}` }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 12, borderTop: `1.5px solid ${theme.colors.ink}` }} />
      <div style={{ position: 'absolute', left: '50%', top: 9, width: 6, height: 8, marginLeft: -3, background: theme.colors.bg, border: `1.5px solid ${theme.colors.ink}`, borderRadius: 1 }} />
    </div>
  );
}

/** Арка недели: квест типографскими днями + сундук-объект + инвентарь заморозок. */
export function WeekCard() {
  const { viewData, addGameEvent } = useBudget();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (!viewData) return null;

  const now = new Date();
  const today = toIso(now);
  const curMonday = weekMonday(now);
  const quest = generateQuest(viewData.categories, now);
  const qs = questState(quest, viewData.operations, now);
  const chest = claimableChest(viewData, now);
  const freezes = freezeInventory(viewData.game_events);
  const balance = coinsBalance(viewData, now);
  const cells = questCells(quest, viewData.operations, curMonday, today);

  // Состояние сундука: ready (можно вскрыть) / open (вскрыт сегодня) / sealed
  const openedToday = (viewData.game_events ?? []).find(
    (e) => e.type === 'chest_open' && createdAtLocalDay(e.created_at) === today,
  );
  const chestState: 'sealed' | 'ready' | 'open' = chest ? 'ready' : openedToday ? 'open' : 'sealed';
  const sealedTease = chestFor(curMonday, viewData.operations, viewData.categories, viewData.settings);
  const openedPayload = (() => {
    try {
      return openedToday ? (JSON.parse(openedToday.payload || '{}') as { freeze?: number }) : {};
    } catch {
      return {};
    }
  })();

  async function openChest() {
    if (!chest || !viewData) return;
    const contents = chestFor(chest.monday, viewData.operations, viewData.categories, viewData.settings);
    setBusy(true);
    try {
      const r = await addGameEvent({
        id: `chest_${chest.monday}`,
        type: 'chest_open',
        month: monthKeyOf(now),
        week: chest.monday,
        coins: contents.coins,
        payload: JSON.stringify({ freeze: contents.freeze ? 1 : 0, lines: contents.lines }),
      });
      if (contents.coins >= 150 || contents.freeze) celebrateBig();
      else celebrateSmall();
      toast.reward({
        icon: 'chest',
        title: 'Сундук недели',
        amount: `+${contents.coins}`,
        sub:
          [contents.freeze ? 'и заморозка в инвентарь' : '', r.synced ? '' : 'синхронизируется позже']
            .filter(Boolean)
            .join(' · ') || undefined,
      });
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function buyFreeze() {
    if (balance < FREEZE_PRICE) {
      toast.show(`нужно ${FREEZE_PRICE} монет — у тебя ${balance}`, 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await addGameEvent({
        id: newClientId(),
        type: 'freeze_purchase',
        month: monthKeyOf(now),
        week: '',
        coins: -FREEZE_PRICE,
        payload: JSON.stringify({ freeze: 1 }),
      });
      toast.reward({
        icon: 'snow',
        title: 'Заморозка куплена',
        amount: `−${FREEZE_PRICE}`,
        sub: `закроет один пропущенный день${r.synced ? '' : ' · синхронизируется позже'}`,
      });
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  const questRewardNote = qs.done ? null : `квест добавит +${quest.reward}`;

  return (
    <div
      style={{
        marginTop: 18,
        border: `1px solid ${theme.colors.line}`,
        borderRadius: theme.radius.sm,
        padding: '12px 14px',
      }}
    >
      {/* Квест */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 15 }}>
          <Icon name="target" size={14} inline /> {quest.label}
        </span>
        <span
          style={{ fontSize: 12, color: theme.colors.sub, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        >
          +{quest.reward} <Icon name="coin" size={12} inline />
        </span>
      </div>

      {cells ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          {cells.map((c, i) => (
            <span
              key={i}
              style={{
                fontFamily: theme.fontDisplay,
                fontVariationSettings: "'opsz' 144, 'WONK' 0",
                fontSize: 16,
                color: c.done ? theme.colors.ink : theme.colors.sub,
                borderBottom: c.done
                  ? `2px solid ${theme.colors.ink}`
                  : `1px dashed ${theme.colors.subSoft}`,
                padding: c.done ? '0 2px 2px' : '0 2px 3px',
              }}
            >
              {c.label}
            </span>
          ))}
          <span
            style={{
              fontSize: 12,
              color: qs.done ? theme.colors.green : theme.colors.sub,
              fontStyle: 'italic',
              marginLeft: 6,
            }}
          >
            {qs.done ? 'выполнен — награда в сундуке' : qs.detail}
          </span>
        </div>
      ) : (
        <div
          style={{
            fontSize: 12,
            color: qs.failed ? theme.colors.accent : qs.done ? theme.colors.green : theme.colors.sub,
            fontStyle: 'italic',
            marginTop: 8,
          }}
        >
          {qs.done ? `выполнен — награда в сундуке (${qs.detail})` : qs.detail}
        </div>
      )}

      {/* Сундук */}
      {chestState === 'sealed' && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 16 }}>
          <ChestGlyph state="sealed" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14 }}>
              Сундук недели{' '}
              <span className="num" style={{ fontSize: 16 }}>
                +{sealedTease.coins}
              </span>{' '}
              <Icon name="coin" size={12} inline /> и растёт
            </div>
            <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 2 }}>
              {['запечатан до воскресенья', questRewardNote].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      )}
      {chestState === 'ready' && chest && (
        <div className="rise" style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 16 }}>
          <ChestGlyph state="ready" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14 }}>
              {chest.monday === curMonday ? 'Воскресенье — сундук созрел: ' : 'Сундук прошлой недели ждёт: '}
              <span className="num" style={{ fontSize: 16 }}>
                +{chestFor(chest.monday, viewData.operations, viewData.categories, viewData.settings).coins}
              </span>{' '}
              внутри
            </div>
            <button
              onClick={openChest}
              disabled={busy}
              style={{
                marginTop: 7,
                padding: '10px 16px',
                background: theme.colors.ink,
                color: theme.colors.paper,
                border: 'none',
                borderRadius: theme.radius.sm,
                fontFamily: theme.fontDisplay,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Вскрыть сундук недели
            </button>
          </div>
        </div>
      )}
      {chestState === 'open' && openedToday && (
        <div className="rise" style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 16 }}>
          <ChestGlyph state="open" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: theme.colors.green }}>
              Вскрыт:{' '}
              <span className="num" style={{ fontSize: 16 }}>
                +{openedToday.coins}
              </span>{' '}
              <Icon name="coin" size={12} inline />
              {(openedPayload.freeze ?? 0) > 0 && (
                <>
                  {' '}
                  и заморозка <Icon name="snow" size={12} inline /> ×{openedPayload.freeze}
                </>
              )}
            </div>
            <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 2 }}>
              новый сундук начнёт копиться в понедельник
            </div>
          </div>
        </div>
      )}

      {/* Инвентарь заморозок */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button
          onClick={buyFreeze}
          disabled={busy}
          title="закрывает один пропущенный день"
          style={{
            padding: '7px 10px',
            background: 'transparent',
            color: theme.colors.sub,
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.sm,
            fontFamily: theme.font,
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Icon name="snow" size={13} inline /> ×{freezes} · {FREEZE_PRICE}
        </button>
      </div>
    </div>
  );
}
